import { getTokenExpiryInSeconds, isTokenNearExpiry } from "@src/appV2/Auth/cognito/tokenUtils";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";

/**
 * Token expiry threshold in seconds.
 * Tokens will be proactively refreshed when they're within this
 * many seconds of expiring, to avoid edge cases where a token
 * expires between validation and API call completion.
 */
const TOKEN_EXPIRY_THRESHOLD_SECONDS = 60;

/**
 * Gets a Cognito ID token with proactive refresh to work around an Amplify v6
 * bug where fetchAuthSession() doesn't auto-refresh tokens for
 * CUSTOM_WITHOUT_SRP auth flow.
 *
 * Accesses the Cognito singleton directly so callers don't need to manage
 * the instance themselves.
 *
 * @see https://github.com/aws-amplify/amplify-js/issues/14618
 */
export async function getCognitoIdTokenWithProactiveRefresh(
  forceRefresh?: boolean
): Promise<string | undefined> {
  const cognitoAuth = getCognitoSingleton();

  if (forceRefresh) {
    return await cognitoAuth.getIdToken(true);
  }

  const cognitoToken = await cognitoAuth.getIdToken(false);
  if (!cognitoToken) {
    return undefined;
  }

  if (isTokenNearExpiry(cognitoToken, TOKEN_EXPIRY_THRESHOLD_SECONDS)) {
    const expiryInSeconds = getTokenExpiryInSeconds(cognitoToken);
    logEvent(APP_V2_APP_EVENTS.AUTH_TOKEN_PROACTIVE_REFRESH, {
      tokenExpiryInSeconds: expiryInSeconds,
      thresholdSeconds: TOKEN_EXPIRY_THRESHOLD_SECONDS,
    });

    const refreshedToken = await cognitoAuth.getIdToken(true);
    if (refreshedToken) {
      return refreshedToken;
    }

    // If refresh fails but the token is still valid, keep using it
    if (expiryInSeconds > 0) {
      return cognitoToken;
    }

    return undefined;
  }

  return cognitoToken;
}
